import { useEffect, useRef, useState } from 'react';
import { Brain } from '@/sdk/brain';
import type { BrainError } from '@/sdk/brain';

interface Message {
  role: 'user' | 'assistant' | 'system';
  text: string;
  ts: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export function JemmaDrawer({ open, onClose }: Props) {
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'system',
      text: 'Jemma is ready. This is the M0 smoke test for the Brain SDK end-to-end. Type a message.',
      ts: new Date().toISOString(),
    },
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [threadId, setThreadId] = useState<string | undefined>();
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  async function send() {
    const text = input.trim();
    if (!text || sending) return;

    const userMsg: Message = { role: 'user', text, ts: new Date().toISOString() };
    setMessages((m) => [...m, userMsg]);
    setInput('');
    setSending(true);

    try {
      const result = await Brain.chat({ threadId, message: text });
      setThreadId(result.threadId);
      setMessages((m) => [
        ...m,
        { role: 'assistant', text: result.reply, ts: new Date().toISOString() },
      ]);
    } catch (err) {
      const brainErr = err as BrainError;
      const detail =
        brainErr.type === 'upstream_down'
          ? 'Worker unreachable. Start it: `npm run worker:dev`.'
          : brainErr.type === 'auth_failure' || brainErr.message?.includes('ANTHROPIC_API_KEY')
            ? 'Anthropic key not set. Run: `cd worker && npx wrangler secret put ANTHROPIC_API_KEY` (or use a .dev.vars file for local dev).'
            : brainErr.message;
      setMessages((m) => [
        ...m,
        {
          role: 'system',
          text: `⚠ ${detail}`,
          ts: new Date().toISOString(),
        },
      ]);
    } finally {
      setSending(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed bottom-0 right-0 w-full sm:w-96 h-2/3 sm:h-3/4 bg-paper border-l border-t border-line-dark shadow-2xl flex flex-col z-50">
      <header className="bg-navy text-white px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-orange font-bold">●</span>
          <span className="font-semibold text-sm">Jemma</span>
          <span className="text-xs opacity-70">active</span>
        </div>
        <button onClick={onClose} className="text-sm hover:opacity-80" aria-label="Close drawer">
          ✕
        </button>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2">
        {messages.map((m, i) => (
          <div
            key={i}
            className={
              m.role === 'user'
                ? 'text-right'
                : m.role === 'system'
                  ? 'text-center text-text-mute italic text-xs'
                  : ''
            }
          >
            <div
              className={`inline-block px-3 py-2 rounded text-sm whitespace-pre-wrap ${
                m.role === 'user'
                  ? 'bg-orange text-white'
                  : m.role === 'assistant'
                    ? 'bg-bg text-text'
                    : 'bg-transparent'
              }`}
              style={{ maxWidth: '85%' }}
            >
              {m.text}
            </div>
          </div>
        ))}
        {sending && <div className="text-xs text-text-mute italic">Jemma is thinking…</div>}
      </div>

      <div className="border-t border-line p-2 flex gap-2">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && send()}
          disabled={sending}
          placeholder="Ask Jemma…"
          className="flex-1 px-2 py-1 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
        />
        <button
          onClick={send}
          disabled={sending || !input.trim()}
          className="px-3 py-1 bg-orange text-white text-sm rounded disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Send
        </button>
      </div>
    </div>
  );
}
